import React from 'react';
import { Box, Button, Collapse, Typography } from '@mui/material';

interface ErrorBoundaryProps {
    children: React.ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
    componentStack: string | null;
    showDetails: boolean;
    copied: boolean;
}

// Catches uncaught render/commit-phase errors from the routed page content so
// one page crashing doesn't blank the entire app (sidebar/nav stay usable).
// Shows the actual error (message + stack) so the user can report it
// accurately instead of just "the page went blank" - it's also logged to the
// console for anyone debugging directly.
// Resets automatically on navigation (see index.tsx, which remounts this via
// a location-keyed wrapper) so the user isn't stuck on the fallback screen
// after the crash is behind them.
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, error: null, componentStack: null, showDetails: false, copied: false };
    }

    static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, info: React.ErrorInfo) {
        console.error('Uncaught error in page content:', error, info.componentStack);
        this.setState({ componentStack: info.componentStack ?? null });
    }

    handleCopy = () => {
        const { error, componentStack } = this.state;
        const report = [
            `Error: ${error?.message ?? 'Unknown error'}`,
            error?.stack ? `\nStack:\n${error.stack}` : '',
            componentStack ? `\nComponent stack:${componentStack}` : '',
            `\nURL: ${window.location.href}`,
            `Time: ${new Date().toISOString()}`,
        ].join('');
        navigator.clipboard.writeText(report).then(() => {
            this.setState({ copied: true });
            setTimeout(() => this.setState({ copied: false }), 2000);
        }).catch(() => {});
    };

    render() {
        if (this.state.hasError) {
            const { error, componentStack, showDetails, copied } = this.state;
            return (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '80vh', gap: 2, px: 3, textAlign: 'center' }}>
                    <Typography variant="h6" fontWeight={600}>Something went wrong loading this page.</Typography>
                    <Typography color="text.secondary" fontSize={14}>
                        Try reloading. If this keeps happening, share the error details below when reporting it.
                    </Typography>
                    <Box
                        sx={{
                            maxWidth: 640, width: '100%', background: '#fdecea', border: '1px solid #f5c6cb',
                            borderRadius: 2, p: 1.5, textAlign: 'left',
                        }}
                    >
                        <Typography sx={{ fontFamily: 'monospace', fontSize: 13, color: '#611a15', wordBreak: 'break-word' }}>
                            {error?.message ?? 'Unknown error'}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Button variant="contained" onClick={() => window.location.reload()} sx={{ background: 'var(--color-primary)', '&:hover': { background: 'var(--color-primary-hover)' } }}>
                            Reload Page
                        </Button>
                        <Button variant="outlined" onClick={() => this.setState(prev => ({ showDetails: !prev.showDetails }))}>
                            {showDetails ? 'Hide' : 'Show'} Details
                        </Button>
                        <Button variant="outlined" onClick={this.handleCopy}>
                            {copied ? 'Copied!' : 'Copy Error Details'}
                        </Button>
                    </Box>
                    <Collapse in={showDetails} sx={{ width: '100%', maxWidth: 640 }}>
                        <Box
                            sx={{
                                background: '#1e1e1e', color: '#d4d4d4', borderRadius: 2, p: 2, mt: 1,
                                textAlign: 'left', maxHeight: 320, overflow: 'auto',
                            }}
                        >
                            <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: 11.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0 }}>
                                {error?.stack ?? error?.message ?? 'No stack trace available'}
                                {componentStack ? `\n\nComponent stack:${componentStack}` : ''}
                            </Typography>
                        </Box>
                    </Collapse>
                </Box>
            );
        }
        return this.props.children;
    }
}

export default ErrorBoundary;
